import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { SUB_ITEM_TYPES } from './constants';

export const SubItemInput = ({ item, index, onUpdate, onRemove, canRemove }) => {
  const itemConfig = SUB_ITEM_TYPES.find(t => t.value === item.item_type) || SUB_ITEM_TYPES[0];
  const IconComponent = itemConfig.icon;
  const showMirror = item.item_type === 'dresser' || item.item_type === 'vanity';
  const showShutterAddons = item.item_type === 'wardrobe';
  const [showAddons, setShowAddons] = React.useState(false);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
      {/* Sub-item Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <IconComponent className="w-4 h-4 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={item.item_name || itemConfig.label}
              onChange={(e) => onUpdate(index, 'item_name', e.target.value)}
              className="font-medium text-sm bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary focus:outline-none px-1"
              placeholder="Item Name"
            />
            {/* Show dimensions next to item name */}
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
              {item.frontage_length}ft × {item.height}ft
            </span>
          </div>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove item"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Item Inputs Grid */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-gray-600">Type</Label>
          <Select 
            value={item.item_type} 
            onValueChange={(v) => onUpdate(index, 'item_type', v)}
          >
            <SelectTrigger className="rounded-lg mt-1 h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUB_ITEM_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-600">Width (ft)</Label>
          <Input
            type="number"
            min="1"
            step="0.5"
            className="rounded-lg mt-1 h-9 text-sm"
            value={item.frontage_length}
            onChange={(e) => onUpdate(index, 'frontage_length', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600">Height (ft)</Label>
          <Input
            type="number"
            min="1"
            step="0.5"
            className="rounded-lg mt-1 h-9 text-sm"
            value={item.height}
            onChange={(e) => onUpdate(index, 'height', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>

      {/* Add-ons Row */}
      <div className="flex flex-wrap gap-3 mt-3">
        <label className="flex items-center gap-2 cursor-pointer px-2 py-1.5 bg-amber-50 rounded-lg border border-amber-200">
          <input
            type="checkbox"
            checked={item.add_profile_lighting || false}
            onChange={(e) => onUpdate(index, 'add_profile_lighting', e.target.checked)}
            className="w-3.5 h-3.5 rounded border-gray-300 text-amber-600"
          />
          <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-xs font-medium text-amber-800">Profile Light</span>
        </label>
        
        {showMirror && (
          <label className="flex items-center gap-2 cursor-pointer px-2 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
            <input
              type="checkbox"
              checked={item.add_mirror || false}
              onChange={(e) => onUpdate(index, 'add_mirror', e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600"
            />
            <span className="text-xs font-medium text-blue-800">Add Mirror</span>
          </label>
        )}

        {/* Shutter Add-ons Toggle for Wardrobes */}
        {showShutterAddons && (
          <button
            type="button"
            onClick={() => setShowAddons(!showAddons)}
            className="flex items-center gap-1 px-2 py-1.5 bg-purple-50 rounded-lg border border-purple-200 text-xs font-medium text-purple-800"
          >
            Shutter Options
            {showAddons ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* Shutter Add-ons Expanded */}
      {showShutterAddons && showAddons && (
        <div className="mt-3 p-3 bg-purple-50/50 rounded-lg border border-purple-200">
          <p className="text-xs font-medium text-purple-800 mb-2">Shutter Add-ons (Optional)</p>
          <div className="flex flex-wrap gap-2">
            <label className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border cursor-pointer transition-all ${
              item.shutter_addons?.glass ? 'border-primary bg-white' : 'border-gray-200 bg-white'
            }`}>
              <input
                type="checkbox"
                checked={item.shutter_addons?.glass || false}
                onChange={(e) => onUpdate(index, 'shutter_addons', { ...(item.shutter_addons || {}), glass: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-primary"
              />
              <span className="text-xs">Glass Shutters</span>
            </label>
            
            <label className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border cursor-pointer transition-all ${
              item.shutter_addons?.traditional ? 'border-primary bg-white' : 'border-gray-200 bg-white'
            }`}>
              <input
                type="checkbox"
                checked={item.shutter_addons?.traditional || false}
                onChange={(e) => onUpdate(index, 'shutter_addons', { ...(item.shutter_addons || {}), traditional: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-primary"
              />
              <span className="text-xs">Traditional Shutters</span>
            </label>
            
            <label className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border cursor-pointer transition-all ${
              item.shutter_addons?.acrylic ? 'border-primary bg-white' : 'border-gray-200 bg-white'
            }`}>
              <input
                type="checkbox"
                checked={item.shutter_addons?.acrylic || false}
                onChange={(e) => onUpdate(index, 'shutter_addons', { ...(item.shutter_addons || {}), acrylic: e.target.checked })}
                className="w-3.5 h-3.5 rounded border-gray-300 text-primary"
              />
              <span className="text-xs">Acrylic Shutters</span>
            </label>
          </div>
          <p className="text-xs text-amber-600 mt-2">
            * Configured at Design Center - not included in current quote
          </p>
        </div>
      )}

      {/* Description */}
      <p className="text-xs text-gray-500 mt-2 italic">
        {itemConfig.description}
      </p>
    </div>
  );
};

export default SubItemInput;
